import { RetryLink } from "@apollo/client/link/retry/retryLink";
import { getMainDefinition } from "@apollo/client/utilities/graphql/getFromAST";
import { isTransientTransportError } from "./transientTransportError";

/** Attempts per operation, the first one included: four retries. */
export const RETRY_MAX_ATTEMPTS = 5;
const RETRY_BASE_DELAY_MS = 1000;

/**
 * A value in [0, 1) from the cryptographic source, so the delay is not
 * flagged as weak randomness. The quality of the jitter does not matter here.
 */
function randomFraction(): number {
  const [value = 0] = crypto.getRandomValues(new Uint32Array(1));
  return value / 2 ** 32;
}

/**
 * Delay before retry number `retry` (1-based): a value in `[d / 2, d]` with
 * `d = 1 s * 2^(retry - 1)`, i.e. about 0.5–1, 1–2, 2–4 and 4–8 s. The lower
 * bound gives a restarting backend at least 7.5 s over four retries; the
 * jitter spreads the retries of many clients.
 */
export function retryDelayMs(
  retry: number,
  random: () => number = randomFraction,
): number {
  const ceiling = RETRY_BASE_DELAY_MS * 2 ** Math.max(0, retry - 1);
  return ceiling / 2 + random() * (ceiling / 2);
}

/**
 * Repeats HTTP operations that failed on the way to the backend (see
 * `isTransientTransportError`). A mutation is repeated only when the proxy
 * answered 503, so it never runs twice.
 */
export const makeRetryLink = () => {
  return new RetryLink({
    delay: (count) => retryDelayMs(count),
    attempts: {
      max: RETRY_MAX_ATTEMPTS,
      retryIf: (error, operation) => {
        const definition = getMainDefinition(operation.query);
        const operationType =
          definition.kind === "OperationDefinition"
            ? definition.operation
            : "query";
        return isTransientTransportError(error, operationType);
      },
    },
  });
};
